import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, CheckCircle2, AlertTriangle, Eye, EyeOff } from "lucide-react";
import SEO from "../components/SEO";
import { formatApiError } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function evaluatePolicy(pw) {
  const checks = [
    { ok: pw.length >= 10, label: "Minimaal 10 tekens" },
    { ok: /[A-Z]/.test(pw), label: "Minimaal één hoofdletter" },
    { ok: /[a-z]/.test(pw), label: "Minimaal één kleine letter" },
    { ok: /\d/.test(pw), label: "Minimaal één cijfer" },
    { ok: /[^A-Za-z0-9]/.test(pw), label: "Minimaal één speciaal teken" },
  ];
  return checks;
}

export default function AdminResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const checks = useMemo(() => evaluatePolicy(pw), [pw]);
  const allOk = checks.every((c) => c.ok);
  const matches = pw && pw === pw2;
  const tokenMissing = !token || token.length < 10;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (tokenMissing) {
      setError("Resetlink ontbreekt of is ongeldig. Vraag een nieuwe link aan.");
      return;
    }
    if (!allOk) {
      setError("Je nieuwe wachtwoord voldoet niet aan alle eisen.");
      return;
    }
    if (!matches) {
      setError("Wachtwoorden komen niet overeen.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/auth/reset-password`,
        { token, new_password: pw, confirm_password: pw2 },
        { headers: { "Content-Type": "application/json" } },
      );
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err) || "Reset mislukt. De link is mogelijk verlopen of al gebruikt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Wachtwoord wijzigen | 5999Cargo" description="Backoffice nieuw wachtwoord instellen." />
      <section className="min-h-[80vh] flex items-center bg-[#F8F9FA]" data-testid="admin-reset-password-page">
        <div className="max-w-md mx-auto w-full px-4">
          <div className="bg-white border border-gray-200 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#082c50] flex items-center justify-center">
                <Lock className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-chivo font-black text-xl text-[#082c50]">Nieuw wachtwoord instellen</div>
                <div className="text-xs text-gray-500">5999Cargo Backoffice</div>
              </div>
            </div>

            {success ? (
              <div className="space-y-5" data-testid="admin-reset-password-success">
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 text-sm flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    Uw wachtwoord is gewijzigd. U kunt nu opnieuw inloggen.
                  </div>
                </div>
                <Button
                  type="button"
                  data-testid="admin-reset-password-login-cta"
                  className="w-full bg-[#082c50] hover:bg-[#082c50] text-white rounded-xl font-chivo font-bold uppercase text-sm py-6"
                  onClick={() => navigate("/admin/login", { replace: true })}
                >
                  Inloggen
                </Button>
              </div>
            ) : tokenMissing ? (
              <div className="space-y-5" data-testid="admin-reset-password-no-token">
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    Deze resetlink is ongeldig of onvolledig. Vraag een nieuwe resetlink aan.
                  </div>
                </div>
                <Button
                  type="button"
                  data-testid="admin-reset-password-request-new"
                  className="w-full bg-[#082c50] hover:bg-[#082c50] text-white rounded-xl font-chivo font-bold uppercase text-sm py-6"
                  onClick={() => navigate("/admin/forgot-password", { replace: true })}
                >
                  Nieuwe link aanvragen
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-reset-password-form">
                <p className="text-sm text-gray-600">
                  Kies een nieuw wachtwoord. Bestaande sessies van dit account worden uit
                  veiligheidsoverwegingen automatisch beëindigd.
                </p>
                <div>
                  <Label className="text-xs tracking-wide uppercase font-chivo font-bold text-gray-700">
                    Nieuw wachtwoord
                  </Label>
                  <div className="mt-1 relative">
                    <Input
                      data-testid="admin-reset-password-input"
                      type={reveal ? "text" : "password"}
                      required
                      autoComplete="new-password"
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      className="rounded-xl border-gray-300 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setReveal((r) => !r)}
                      data-testid="admin-reset-password-reveal"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                      aria-label={reveal ? "Verberg wachtwoord" : "Toon wachtwoord"}
                    >
                      {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs tracking-wide uppercase font-chivo font-bold text-gray-700">
                    Herhaal nieuw wachtwoord
                  </Label>
                  <Input
                    data-testid="admin-reset-password-input-confirm"
                    type={reveal ? "text" : "password"}
                    required
                    autoComplete="new-password"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    className="mt-1 rounded-xl border-gray-300"
                  />
                </div>

                <ul className="text-xs space-y-1" data-testid="admin-reset-password-checks">
                  {checks.map((c) => (
                    <li
                      key={c.label}
                      className={`flex items-center gap-2 ${c.ok ? "text-green-700" : "text-gray-500"}`}
                      data-testid={`admin-reset-policy-${c.ok ? "ok" : "todo"}`}
                    >
                      <span className={`inline-block w-2 h-2 rounded-full ${c.ok ? "bg-green-500" : "bg-gray-300"}`} />
                      {c.label}
                    </li>
                  ))}
                  <li
                    className={`flex items-center gap-2 ${matches ? "text-green-700" : "text-gray-500"}`}
                    data-testid="admin-reset-policy-match"
                  >
                    <span className={`inline-block w-2 h-2 rounded-full ${matches ? "bg-green-500" : "bg-gray-300"}`} />
                    Wachtwoorden komen overeen
                  </li>
                </ul>

                {error && (
                  <div
                    data-testid="admin-reset-password-error"
                    className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm"
                  >
                    {error}
                  </div>
                )}

                <Button
                  data-testid="admin-reset-password-submit"
                  type="submit"
                  disabled={loading || !allOk || !matches}
                  className="w-full bg-[#082c50] hover:bg-[#082c50] text-white rounded-xl font-chivo font-bold uppercase text-sm py-6"
                >
                  {loading ? "Bezig..." : "Wachtwoord wijzigen"}
                </Button>

                <div className="text-center pt-1">
                  <Link
                    to="/admin/login"
                    data-testid="admin-reset-password-back-link"
                    className="text-xs text-[#1a5193] hover:underline font-chivo tracking-wide"
                  >
                    Terug naar inloggen
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
